import KitsForm from '@/components/Operational/Kits/KitsForm';
import { SaveButton, TabContentWrapper } from '@/components/_common';
import ButtonBack from '@/components/_common/ButtonBack';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useEditKit } from '@/core/domain/operational/Kits/Kits.hooks';
import {
  EditKitRequest,
  KitCreateFormValues,
  KitItemCreate,
  KitItemEdit,
  KitItemType,
} from '@/core/domain/operational/Kits/Kits.types';
import { useWorkOrderKitDetail } from '@/core/domain/workorder/Kits/Kits.hooks';
import Authorities from '@/types/Authority';
import { PageProps } from '@/types/PageProps';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Divider, Form, Skeleton } from 'antd';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

interface KitItemsContextProps {
  items: KitItemCreate[];
  setItems: React.Dispatch<React.SetStateAction<KitItemCreate[]>>;
}

const KitItemsContext = createContext({} as KitItemsContextProps);
export const useKitItemsContextEdit = () => useContext(KitItemsContext);

const KitsDetailPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const params = useParams<{ kitId: string }>();

  const [form] = Form.useForm<KitCreateFormValues>();
  const [items, setItems] = useState<KitItemCreate[]>([]);

  const {
    data: kit,
    dataUpdatedAt,
    isLoading: loadingDetail,
  } = useWorkOrderKitDetail({ kitId: Number(params.kitId) });

  const { isLoading: loadingEdit, mutateAsync: editKitMutate } = useEditKit();

  useEffect(() => {
    if (kit) {
      form.setFieldsValue({
        description: kit.description,
        increaseDecrease: kit.increaseDecrease,
      });

      setItems(
        (kit.items.map((item) => ({
          id: item.id,
          itemId: item.item.itemId,
          description: item.item.name,
          quantity: item.quantity,
          itemType: item.item.type === 1 ? KitItemType.part : KitItemType.task,
          manufacturingCode: item.item.manufacturingCode,
          sku: item.item.sku,
          barcode: item.item.barCode,
          manufacturer: { name: item.item.manufacturerName },
          companies: item.discounts.map((discount) => ({
            id: discount.id,
            companyId: discount.company.id,
            name: discount.company.name,
            price: item.prices.find(
              (price) => price.companyId === discount.company.id,
            )?.price,
            increaseDecrease: discount.increaseDecrease,
          })),
          increaseDecrease: kit.increaseDecrease || undefined,
        })) as KitItemEdit[]) || [],
      );
    }
  }, [kit, dataUpdatedAt]);

  async function handleEdit() {
    const values = await form.validateFields();

    const obj: EditKitRequest = {
      description: values.description,
      id: Number(params.kitId),
      increaseDecrease: values.increaseDecrease,
      items: items.map((item) => ({
        id: kit?.items.find((itemKit) => itemKit.item.itemId === item.itemId)
          ?.id,
        itemId: item.itemId,
        itemType: item.itemType,
        quantity: item.quantity,
        kitDiscount: item.companies.map((company) => ({
          id: company.id,
          companyId: company.companyId,
          increaseDecrease:
            typeof company.increaseDecrease === 'number'
              ? company.increaseDecrease
              : 0,
        })),
      })),
    };

    await editKitMutate(obj);
  }

  return (
    <KitItemsContext.Provider value={{ items, setItems }}>
      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SaveButton
            type="primary"
            block
            onClick={handleEdit}
            disabled={loadingDetail || !hasPermissions([Authorities.KITS_EDIT])}
            loading={loadingEdit}
          >
            {intl.formatMessage({ id: 'commons.form.button.save' })}
          </SaveButton>
        }
      >
        <Skeleton loading={loadingEdit || loadingDetail}>
          <Form form={form}>
            <KitsForm isEdit kit={kit} />
          </Form>

          <Divider />

          <SpaceForButton>
            <ButtonBack path="/app/operational/kits" />
          </SpaceForButton>
        </Skeleton>
      </TabContentWrapper>
    </KitItemsContext.Provider>
  );
};

export default KitsDetailPage;
